import { formatCurrency, useTranslate } from "helpers";
import { VehicleSpecificationProps } from "../types";
import Tooltip from "ui/components/Tooltip/Tooltip";

const VehicleHeader: React.FC<VehicleSpecificationProps> = ({ fleetVehicleDetail }) => {
    const { t } = useTranslate();

    const price = fleetVehicleDetail?.price;
    const netPrice = price?.netPrice ?? 0;
    const specialTax = price?.specialTax ?? 0;
    const totalPurchase = price?.totalPurchasePrice ?? 0;
    const sellingPrice = price?.sellingPrice ?? 0;
    const margin = price?.margin ?? 0;
    const marginPct = price?.marginPercentage ?? 0;
    const isPositiveMargin = margin > 0;
    const totalExpenses = price?.totalExpenses ?? 0;
    const netMargin = price?.netMargin ?? margin;
    const netMarginPct = price?.netMarginPercentage;
    const isPositiveNetMargin = netMargin > 0;


    return (
        <div className="border-b border-slate-200 pt-4 px-4">
            <div className="flex justify-between items-start mb-4">
                <div className="flex gap-2 items-center">
                    <div className="relative group">
                        <div
                            className="w-8 h-8 rounded-full border border-gray-300 cursor-pointer"
                            style={{ background: fleetVehicleDetail?.vehicleColorId ?? "" }}
                        ></div>
                        <span className="absolute left-1/2 -translate-x-1/2 -top-9 bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                            {t("admin.fleet.vehicle.detail.info.color")}: {fleetVehicleDetail?.colorName ?? ""}
                        </span>
                    </div>
                    <div className="max-w-xl">
                        <div className="text-md font-bold text-slate-600 truncate">
                            {fleetVehicleDetail.name}
                        </div>
                        <div className="text-sm text-slate-500">
                            {fleetVehicleDetail?.price?.vehicleInVatSystem ? <>Vozilo je u sustavu PDV-a</> : <>Vozilo <span className="font-bold">nije</span> u sustavu PDV-a</>}
                        </div>
                    </div>
                </div>
                <div className="flex gap-6 items-start">
                    <div className="flex gap-4 items-end">
                        <div>
                            <div className="text-xs text-slate-500 uppercase">
                                {t("admin.fleet.vehicle.detail.info.specialtax")}
                            </div>
                            <div className="text-lg font-semibold text-gray-600">
                                {formatCurrency.format(specialTax)}
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200"></div>
                        <div>
                            <div className="text-xs text-slate-500 uppercase">
                                {t("admin.fleet.vehicle.detail.info.netprice")}
                            </div>
                            <div className="text-lg font-semibold text-gray-600">
                                {formatCurrency.format(netPrice)}
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200"></div>
                        <div>
                            <div className="text-xs text-amber-600 uppercase font-semibold">
                                Ukupna nabavna
                            </div>
                            <div className="text-lg font-bold text-amber-600">
                                {formatCurrency.format(totalPurchase)}
                            </div>
                        </div>
                        <div className="w-px h-10 bg-gray-200"></div>
                        <div>
                            <div className="text-xs text-green-600  font-semibold">
                                <Tooltip
                                    decorate={
                                        <span className="uppercase">
                                            {t("admin.fleet.vehicle.detail.info.sellingsrice")}
                                        </span>
                                    }
                                    position="bottom-right"
                                    size="medium"
                                >
                                    <div className="min-w-[520px]">
                                        <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Pregled zarade</div>
                                        <div className="flex gap-4 items-stretch">
                                            <div className="flex-1 min-w-[240px] flex flex-col">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-300">Ukupna nabavna</span>
                                                    <span className="font-medium">{formatCurrency.format(totalPurchase)}</span>
                                                </div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-300">Prodajna cijena</span>
                                                    <span className="font-medium">{formatCurrency.format(sellingPrice)}</span>
                                                </div>
                                                <div className="border-t border-slate-600 my-2"></div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-300">Bruto marža</span>
                                                    <span className={`font-bold ${isPositiveMargin ? "text-green-400" : "text-red-400"}`}>
                                                        {formatCurrency.format(margin)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-300">Bruto marža %</span>
                                                    <span className={`font-bold ${isPositiveMargin ? "text-green-400" : "text-red-400"}`}>
                                                        {marginPct.toFixed(2)} %
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="w-px bg-slate-600"></div>
                                            <div className="flex-1 min-w-[240px] flex flex-col justify-end">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-300">Troškovi</span>
                                                    <span className="font-medium text-rose-400">
                                                        -{formatCurrency.format(totalExpenses)}
                                                    </span>
                                                </div>
                                                <div className="border-t border-slate-600 my-2"></div>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-300">Neto marža</span>
                                                    <span className={`font-bold ${isPositiveNetMargin ? "text-green-400" : "text-red-400"}`}>
                                                        {formatCurrency.format(netMargin)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-slate-300">Neto marža %</span>
                                                    <span className={`font-bold ${isPositiveNetMargin ? "text-green-400" : "text-red-400"}`}>
                                                        {netMarginPct != null ? `${netMarginPct.toFixed(2)} %` : "—"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Tooltip>
                            </div>
                            <div className="text-lg font-bold text-green-600">
                                {formatCurrency.format(sellingPrice)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VehicleHeader;
